import React, { useCallback, useEffect, useState } from "react";
import { Form, Input, Select, Button, message } from "antd";
import { INDUSTRY_OPTIONS } from "common/enums/Industry.enum";
import { CompanyInfoType } from "common/types/companyInfo.type";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "store/store";
import { useCommonContext } from "contexts/CommonContext";
import { changeCompanyName } from "reducers/authSlice";
import InformationTabsLayout from "components/companyProfile/InformationTabsLayout";
import { useSearchParams } from "react-router-dom";
import {
  useGetCompanyProfileQuery,
  useUpdateCompanyProfileMutation,
} from "reducers/companyProfileApi";

const BuyerRegisterInfo = () => {
  const { setHeaderTitle } = useCommonContext();
  const [form] = Form.useForm();
  const [errors, setErrors] = useState<{ [key: string]: boolean }>({});
  const { userData } = useSelector((state: RootState) => state.auth);
  const [searchParams] = useSearchParams();
  const profileCompanyCode = searchParams.get("companyCode") || userData.code;
  const canEditProfileAvatar = profileCompanyCode === userData.code;
  const profileRole = canEditProfileAvatar ? "buyer" : "seller";
  const dispatch = useDispatch<AppDispatch>();
  const { data: companyProfileData } = useGetCompanyProfileQuery(
    { companyCode: userData.code },
    { skip: !userData.code },
  );
  const [updateCompanyProfile] = useUpdateCompanyProfileMutation();
  const companyCurrentDetail = companyProfileData?.data;
  const initialData: CompanyInfoType = {
    company_name: "",
    postal_code: "",
    prefecture: "",
    address: "",
    building_room: "",
    industry: "",
    contact_last_name: "",
    contact_first_name: "",
    phone_number: "",
    email: "",
  };

  // useEffect
  useEffect(() => {
    setHeaderTitle("登録情報の管理");
  }, []);

  const fetchUpdateCompanyInfo = async (data: CompanyInfoType) => {
    try {
      await updateCompanyProfile({
        companyCode: userData.code,
        data,
      }).unwrap();
      message.success("情報を更新しました");
      if (data.company_name) {
        dispatch(changeCompanyName(data.company_name));
      }
    } catch {
      message.error("情報の更新に失敗しました");
    }
  };

  // Handle actions
  const handleSave = async (values: CompanyInfoType) => {
    await fetchUpdateCompanyInfo(values);
  };

  const resetUnsavedChanges = useCallback(() => {
    form.resetFields();
    if (companyCurrentDetail) {
      const formData: CompanyInfoType = {
        company_name: companyCurrentDetail.company_name,
        postal_code: companyCurrentDetail.postal_code,
        prefecture: companyCurrentDetail.prefecture,
        address: companyCurrentDetail.address,
        building_room: companyCurrentDetail.building_room,
        industry: companyCurrentDetail.industry,
        contact_last_name: companyCurrentDetail.contact_last_name,
        contact_first_name: companyCurrentDetail.contact_first_name,
        phone_number: companyCurrentDetail.phone_number,
        email: companyCurrentDetail.email,
      };
      form.setFieldsValue(formData);
    }

    setErrors({});
  }, [companyCurrentDetail, form]);

  useEffect(() => {
    resetUnsavedChanges();
  }, [resetUnsavedChanges]);

  const handleCancel = () => {
    resetUnsavedChanges();
    message.info("変更をキャンセルしました");
  };

  return (
    <InformationTabsLayout
      canEditAvatar={canEditProfileAvatar}
      companyCode={profileCompanyCode}
      onReturnToInfo={resetUnsavedChanges}
      profileOnly={!canEditProfileAvatar}
      role={profileRole}
    >
      <div className="flex w-full flex-col items-start gap-[30px] md:gap-12">
        <div className="self-stretch flex flex-col justify-start items-start gap-[30px] md:gap-10">
          <div className="self-stretch flex flex-col justify-start items-start gap-[20px] md:gap-7">
            <div
              data-プロパティ1="base"
              className="self-stretch py-2.5 border-b border-neutral-border inline-flex justify-start items-center gap-3.5"
            >
              <div className="w-1 self-stretch bg-primary rounded-sm" />
              <div className="flex-1 flex justify-start items-center gap-2.5">
                <div className="flex justify-start items-center gap-[5px]">
                  <div className="justify-start text-primary text-[18px] md:text-xl font-bold leading-9">
                    登録情報
                  </div>
                </div>
              </div>
            </div>

            <Form
              form={form}
              initialValues={initialData}
              onFinish={handleSave}
              className="w-full max-w-[800px]"
            >
              <div className="self-stretch bg-white flex flex-col justify-start items-start gap-3.5">
                {/* 法人名 */}
                <div className="self-stretch md:inline-flex justify-start items-start gap-5">
                  <div className="w-44 py-3 flex justify-start items-start gap-2.5">
                    <div className="justify-start text-black text-[15px] md:text-base font-medium leading-relaxed">
                      法人名
                    </div>
                  </div>
                  <div className="w-full md:w-auto flex-1 inline-flex flex-col justify-start items-start gap-[5px]">
                    <Form.Item
                      name="company_name"
                      className="!mb-0 w-full"
                      rules={[
                        { required: true, message: "法人名を入力してください" },
                      ]}
                    >
                      <Input
                        size="large"
                        status={errors.company_name ? "error" : undefined}
                        className="!h-12"
                      />
                    </Form.Item>
                    <div className="md:hidden self-stretch justify-start text-black text-[11px] md:text-xs font-normal leading-tight">
                      ※変更には再審査が必要です
                    </div>
                  </div>
                </div>

                {/* 郵便番号 */}
                <div className="self-stretch md:inline-flex justify-start items-center gap-5">
                  <div className="w-44 flex justify-start items-center gap-2.5">
                    <div className="justify-start text-black text-[15px] md:text-base font-medium leading-relaxed">
                      郵便番号
                    </div>
                  </div>
                  <div className="w-full md:w-auto flex justify-start items-center gap-3.5">
                    <Form.Item
                      name="postal_code"
                      className="!mb-0"
                      rules={[
                        {
                          required: true,
                          message: "郵便番号を入力してください",
                        },
                      ]}
                    >
                      <Input
                        size="large"
                        placeholder="123-4567"
                        status={errors.postal_code ? "error" : undefined}
                        className="!h-12 w-48"
                      />
                    </Form.Item>
                  </div>
                </div>

                {/* 都道府県 */}
                <div className="self-stretch md:inline-flex justify-start items-center gap-5">
                  <div className="w-44 flex justify-start items-center gap-2.5">
                    <div className="justify-start text-black text-[15px] md:text-base font-medium leading-relaxed">
                      都道府県
                    </div>
                  </div>
                  <div className="w-full md:w-auto flex-1">
                    <Form.Item
                      name="prefecture"
                      className="!mb-0"
                      rules={[
                        {
                          required: true,
                          message: "都道府県を入力してください",
                        },
                      ]}
                    >
                      <Input
                        size="large"
                        status={errors.prefecture ? "error" : undefined}
                        className="!h-12"
                      />
                    </Form.Item>
                  </div>
                </div>

                {/* 市区町村・番地 */}
                <div className="self-stretch md:inline-flex justify-start items-center gap-5">
                  <div className="w-44 flex justify-start items-center gap-2.5">
                    <div className="justify-start text-black text-[15px] md:text-base font-medium leading-relaxed">
                      市区町村・番地
                    </div>
                  </div>
                  <div className="w-full md:w-auto flex-1">
                    <Form.Item
                      name="address"
                      className="!mb-0"
                      rules={[
                        {
                          required: true,
                          message: "市区町村・番地を入力してください",
                        },
                      ]}
                    >
                      <Input
                        size="large"
                        status={errors.address ? "error" : undefined}
                        className="!h-12"
                      />
                    </Form.Item>
                  </div>
                </div>

                {/* 建物名・部屋番号 */}
                <div className="self-stretch md:inline-flex justify-start items-center gap-5">
                  <div className="w-44 flex justify-start items-center gap-2.5">
                    <div className="justify-start text-black text-[15px] md:text-base font-medium leading-relaxed">
                      建物名・部屋番号
                    </div>
                  </div>
                  <div className="w-full md:w-auto flex-1">
                    <Form.Item name="building_room" className="!mb-0">
                      <Input
                        size="large"
                        status={errors.building_room ? "error" : undefined}
                        className="!h-12"
                      />
                    </Form.Item>
                  </div>
                </div>

                {/* 業種 */}
                <div className="self-stretch md:inline-flex justify-start items-center gap-5">
                  <div className="w-44 flex justify-start items-center gap-2.5">
                    <div className="justify-start text-black text-[15px] md:text-base font-medium leading-relaxed">
                      業種
                    </div>
                  </div>
                  <div className="w-full md:w-auto flex-1">
                    <Form.Item
                      name="industry"
                      className="!mb-0"
                      rules={[
                        { required: true, message: "業種を選択してください" },
                      ]}
                    >
                      <Select
                        size="large"
                        options={Object.values(INDUSTRY_OPTIONS)}
                        status={errors.industry ? "error" : undefined}
                        className="!h-12"
                        placeholder="業種を選択"
                      />
                    </Form.Item>
                  </div>
                </div>

                {/* 担当者名 */}
                <div className="self-stretch md:inline-flex justify-start items-center gap-5">
                  <div className="w-44 flex justify-start items-center gap-2.5">
                    <div className="justify-start text-black text-[15px] md:text-base font-medium leading-relaxed">
                      担当者名
                    </div>
                  </div>
                  <div className="flex-1 flex justify-start items-center gap-2.5">
                    <Form.Item
                      name="contact_last_name"
                      className="!mb-0 flex-1"
                      rules={[
                        { required: true, message: "姓を入力してください" },
                      ]}
                    >
                      <Input
                        size="large"
                        placeholder="姓"
                        status={errors.contact_last_name ? "error" : undefined}
                        className="!h-12"
                      />
                    </Form.Item>
                    <Form.Item
                      name="contact_first_name"
                      className="!mb-0 flex-1"
                      rules={[
                        { required: true, message: "名を入力してください" },
                      ]}
                    >
                      <Input
                        size="large"
                        placeholder="名"
                        status={errors.contact_first_name ? "error" : undefined}
                        className="!h-12"
                      />
                    </Form.Item>
                  </div>
                </div>

                {/* 電話番号 */}
                <div className="self-stretch md:inline-flex justify-start items-center gap-5">
                  <div className="w-44 flex justify-start items-center gap-2.5">
                    <div className="justify-start text-black text-[15px] md:text-base font-medium leading-relaxed">
                      電話番号
                    </div>
                  </div>
                  <div className="w-full md:w-auto flex-1">
                    <Form.Item
                      name="phone_number"
                      className="!mb-0"
                      rules={[
                        {
                          required: true,
                          message: "電話番号を入力してください",
                        },
                      ]}
                    >
                      <Input
                        size="large"
                        status={errors.phone_number ? "error" : undefined}
                        className="!h-12"
                      />
                    </Form.Item>
                  </div>
                </div>

                {/* メールアドレス */}
                <div className="self-stretch md:inline-flex justify-start items-start gap-5">
                  <div className="w-44 py-3 flex justify-start items-start gap-2.5">
                    <div className="justify-start text-black text-[15px] md:text-base font-medium leading-relaxed">
                      メールアドレス
                    </div>
                  </div>
                  <div className="w-full md:w-auto flex-1 inline-flex flex-col justify-start items-start gap-[5px]">
                    <Form.Item name="email" className="!mb-0 w-full">
                      <Input
                        size="large"
                        disabled
                        className="!h-12 !bg-neutral-50"
                      />
                    </Form.Item>
                    <div className="self-stretch justify-start text-black text-[11px] md:text-xs font-normal leading-tight">
                      ※ログインIDとして使用されます（変更不可）
                    </div>
                  </div>
                </div>
              </div>
            </Form>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="self-stretch flex flex-col justify-start items-start gap-[15px] md:gap-3.5">
          <div className="self-stretch flex flex-col-reverse md:flex-row md:inline-flex justify-start items-start gap-[10px] md:gap-3.5">
            <Button
              type="default"
              size="large"
              onClick={handleCancel}
              className="w-80 min-h-14 px-5 py-2.5 bg-primary-light rounded-md !text-primary !font-bold !text-[16px] md:!text-lg hover:!bg-primary-light/80"
            >
              変更をキャンセル
            </Button>
            <Button
              type="primary"
              size="large"
              onClick={() => form.submit()}
              className="w-80 min-h-14 px-5 py-2.5 bg-primary rounded-md !text-white !font-bold !text-[16px] md:!text-lg hover:!bg-primary/90"
            >
              情報を保存する
            </Button>
          </div>
        </div>
      </div>
    </InformationTabsLayout>
  );
};

export default BuyerRegisterInfo;
